/**
 * File: PaginationControls.js
 * Origin: isotect
 * Description: PaginationControls component to render pagination control buttons.
 *
 * Version: v1.1.0
 * Created: 2026-04-27
 * Last Updated: 2026-06-23
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Local project files
import DropDownField from "../forms/DropDownField";
import { IconButton, TextButton } from "../ui/Buttons";

// Icons
import {
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
} from "react-icons/fa";
import { HiDotsHorizontal } from "react-icons/hi";

/**
 * Renders pagination control buttons for a table.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {number} props.currentPage - Current page index
 * @param {(page: number) => void} props.setCurrentPage - Callback to update the current page
 * @param {number} [props.rowsPerPage] - Number of rows displayed per page
 * @param {(rows: number) => void} props.setRowsPerPage - Callback to update rows per page
 * @param {number} props.totalTableRows - Total number of rows in the table
 *
 * @returns {JSX.Element} Pagination controls element
 */
function PaginationControls({
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalTableRows,
}) {
    const firstRowNumber = (currentPage - 1) * rowsPerPage + 1;
    const lastRowNumber = Math.min(
        firstRowNumber + rowsPerPage - 1,
        totalTableRows,
    );

    const results = `Results: ${firstRowNumber}-${lastRowNumber} of ${totalTableRows}`;
    const totalPages = Math.ceil(totalTableRows / rowsPerPage);

    let maxButtonsRendered = 5;
    let renderedButtons = [];

    const half = Math.floor(maxButtonsRendered / 2);

    let startPage = Math.max(1, currentPage - half);
    let endPage = startPage + maxButtonsRendered - 1;

    if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxButtonsRendered + 1);
    }

    if (startPage > 1) {
        renderedButtons.push(
            <IconButton icon={HiDotsHorizontal} onClick={null} />,
        );
    }

    for (let page = startPage; page <= endPage; page++) {
        renderedButtons.push(
            <TextButton
                key={page}
                text={`${page}`}
                type={`page`}
                onClick={() => handlePageClick(page)}
                className={currentPage === page ? "page-button--active" : ""}
            />,
        );
    }

    if (endPage < totalPages) {
        renderedButtons.push(
            <IconButton icon={HiDotsHorizontal} onClick={null} />,
        );
    }

    const rowsPerPageConfig = {
        "pagination-10-rows": 10,
        "pagination-25-rows": 25,
        "pagination-50-rows": 50,
        "pagination-100-rows": 100,
        "pagination-250-rows": 250,
    };

    const rowsPerPageSelection = `pagination-${rowsPerPage}-rows`;
    const rowsPerPageOptions = Object.entries(rowsPerPageConfig).map((row) => {
        return {
            id: row[0],
            value: row[1],
        };
    });

    const handlePageClick = (i) => {
        setCurrentPage(i);
    };

    const handleFirstPageChange = () => {
        setCurrentPage(1);
    };

    const handlePreviousPageChange = () => {
        setCurrentPage(Math.max(currentPage - 1, 1));
    };

    const handleNextPageChange = () => {
        setCurrentPage(Math.min(currentPage + 1, totalPages));
    };

    const handleLastPageChange = () => {
        setCurrentPage(totalPages);
    };

    const handleRowsPerPageChange = (selectionID) => {
        setRowsPerPage(rowsPerPageConfig[selectionID]);
        setCurrentPage(1);
    };

    return (
        <>
            <div className="pagination-controls">
                <div className="pagination-controls__left-controls">
                    <p>{results}</p>
                    <div className="pagination-controls__drop-down">
                        <DropDownField
                            label=""
                            options={rowsPerPageOptions}
                            selection={rowsPerPageSelection}
                            setSelection={handleRowsPerPageChange}
                        />
                    </div>
                </div>
                <div className="pagination-controls__right-controls">
                    <IconButton
                        icon={FaAngleDoubleLeft}
                        onClick={handleFirstPageChange}
                    />
                    <IconButton
                        icon={FaAngleLeft}
                        onClick={handlePreviousPageChange}
                    />
                    {renderedButtons}
                    <IconButton
                        icon={FaAngleRight}
                        onClick={handleNextPageChange}
                    />
                    <IconButton
                        icon={FaAngleDoubleRight}
                        onClick={handleLastPageChange}
                    />
                </div>
            </div>
        </>
    );
}

export default PaginationControls;
